import React, { useMemo, useState } from 'react';
import { FaCheck, FaChevronRight, FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import TenantUrlInput, { BHOM_TEMPLATE, ITSM_TEMPLATE } from '../../../../../ReusableBlocks/TenantUrlInput';
import { isTemplateOrContainsToken } from '../../../../../Utils/tenantUrlTemplate';
import { tenantTypeOptions } from '../mockTenantData';
import {
  detectItsmUrlFromTenantUrl,
  getDisplayUrl,
  getInitialOnboardForm,
  ONBOARD_STEPS,
  validateOnboardStep1,
  validateOnboardStep2,
} from '../tenantHelpers';

const INTEGRATION_SECTIONS = [
  {
    key: 'jira',
    label: 'Jira',
    fields: [
      { key: 'jiraUrl', label: 'Jira URL' },
      { key: 'jiraUsername', label: 'Jira Username' },
      { key: 'jiraPassword', label: 'Jira Password', secret: true },
    ],
  },
  {
    key: 'catchpoint',
    label: 'Catchpoint',
    fields: [
      { key: 'catchpointUrl', label: 'Catchpoint URL' },
      { key: 'catchpointUsername', label: 'Catchpoint Username' },
      { key: 'catchpointPassword', label: 'Catchpoint Password', secret: true },
    ],
  },
  {
    key: 'chatops',
    label: 'Teams Credentials',
    fields: [
      { key: 'chatopsUsername', label: 'Teams Username' },
      { key: 'chatopsPassword', label: 'Teams Password', secret: true },
    ],
  },
  {
    key: 'dynatrace',
    label: 'Dynatrace',
    fields: [
      { key: 'dynatraceHost', label: 'Dynatrace Host / URL' },
      { key: 'dynatraceUsername', label: 'Dynatrace Username' },
      { key: 'dynatracePassword', label: 'Dynatrace Password', secret: true },
    ],
  },
];

const ACCESS_FIELDS = [
  { key: 'username', label: 'Username' },
  { key: 'password', label: 'Password', secret: true },
  { key: 'apiKey', label: 'API Key', secret: true },
  { key: 'accessKey', label: 'Access Key', secret: true },
  { key: 'secretKey', label: 'Secret Key', secret: true },
];

const ReviewField = ({ label, value }) => (
  <div className="stm-onboard-review__field">
    <span className="stm-onboard-review__label">{label}</span>
    <span className="stm-onboard-review__value">{value || '—'}</span>
  </div>
);

const ReviewSecretField = ({ label, value, revealed, onToggle }) => (
  <div className="stm-onboard-review__field stm-onboard-review__field--secret">
    <span className="stm-onboard-review__label">{label}</span>
    <div className="stm-onboard-review__secret">
      <span>{revealed ? value : '••••••••••••'}</span>
      <button type="button" className="stm-onboard-review__reveal" onClick={onToggle}>
        {revealed ? 'Hide' : 'Reveal'}
      </button>
    </div>
  </div>
);

const OnboardTenantWizard = ({ isOpen, onClose, onSave, isSaving, onboardError }) => {
  const [form, setForm] = useState(getInitialOnboardForm);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [showSecrets, setShowSecrets] = useState({});
  const [reviewRevealed, setReviewRevealed] = useState({});
  const [openIntegration, setOpenIntegration] = useState('');

  const itsmDetection = useMemo(
    () => detectItsmUrlFromTenantUrl(form.tenantUrl),
    [form.tenantUrl]
  );

  const showItsmHelper = itsmDetection.isDetected
    && form.itsmUrl.trim() === itsmDetection.suggestedUrl.trim();

  if (!isOpen) return null;

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'tenantUrl' && !prev.itsmUrlManuallyEdited) {
        if (isTemplateOrContainsToken(value)) {
          next.itsmUrl = '';
        } else {
          const detection = detectItsmUrlFromTenantUrl(value);
          if (detection.suggestedUrl) {
            next.itsmUrl = detection.suggestedUrl;
          }
        }
      }
      if (field === 'itsmUrl') {
        next.itsmUrlManuallyEdited = true;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateStep = (targetStep) => {
    const nextErrors = targetStep === 1
      ? validateOnboardStep1(form)
      : targetStep === 2
        ? validateOnboardStep2(form)
        : {};
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resetWizard = () => {
    setForm(getInitialOnboardForm());
    setErrors({});
    setStep(1);
    setShowSecrets({});
    setReviewRevealed({});
    setOpenIntegration('');
  };

  const closeWizard = () => {
    resetWizard();
    onClose();
  };

  const goToStep = (targetStep, integrationKey = '') => {
    setStep(targetStep);
    if (integrationKey) setOpenIntegration(integrationKey);
  };

  const submit = async () => {
    if (!validateStep(1) || !validateStep(2)) return;
    try {
      await onSave(form);
      resetWizard();
    } catch {
      // Parent handles error toast; keep form values intact.
    }
  };

  const toggleSecret = (field) => {
    setShowSecrets((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const toggleReviewSecret = (field) => {
    setReviewRevealed((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const renderTextField = (label, field, placeholder = '', required = false) => (
    <label className="stm-onboard-field" key={field}>
      <span className="stm-onboard-field__label">
        {label}
        {required && <span className="stm-onboard-field__required" aria-hidden="true"> *</span>}
      </span>
      <input
        value={form[field]}
        onChange={(event) => updateField(field, event.target.value)}
        placeholder={placeholder}
      />
      {errors[field] && <span className="stm-onboard-field__error">{errors[field]}</span>}
    </label>
  );

  const renderSecretField = (label, field, required = false) => (
    <label className="stm-onboard-field" key={field}>
      <span className="stm-onboard-field__label">
        {label}
        {required && <span className="stm-onboard-field__required" aria-hidden="true"> *</span>}
      </span>
      <div className="stm-onboard-secret">
        <input
          type={showSecrets[field] ? 'text' : 'password'}
          value={form[field]}
          onChange={(event) => updateField(field, event.target.value)}
          autoComplete="new-password"
        />
        <button
          type="button"
          className="stm-icon-btn stm-icon-btn--ghost"
          onClick={() => toggleSecret(field)}
          aria-label={`Toggle ${label}`}
        >
          {showSecrets[field] ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
      {errors[field] && <span className="stm-onboard-field__error">{errors[field]}</span>}
    </label>
  );

  const renderStep1 = () => (
    <div className="stm-onboard-step-content">
      <label className="stm-onboard-field">
        <span className="stm-onboard-field__label">Tenant Type</span>
        <select value={form.tenantType} onChange={(event) => updateField('tenantType', event.target.value)}>
          {tenantTypeOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>
      <label className="stm-onboard-field">
        <span className="stm-onboard-field__label">Tenant URL</span>
        <TenantUrlInput
          value={form.tenantUrl}
          template={BHOM_TEMPLATE}
          onChange={(value) => updateField('tenantUrl', value)}
        />
        {errors.tenantUrl && <span className="stm-onboard-field__error">{errors.tenantUrl}</span>}
      </label>
      <label className="stm-onboard-field">
        <span className="stm-onboard-field__label">ITSM URL</span>
        <TenantUrlInput
          value={form.itsmUrl}
          template={ITSM_TEMPLATE}
          onChange={(value) => updateField('itsmUrl', value)}
        />
        {showItsmHelper && (
          <p className="stm-onboard-helper">Detected from Tenant URL</p>
        )}
        {errors.itsmUrl && <span className="stm-onboard-field__error">{errors.itsmUrl}</span>}
      </label>
      <label className="stm-onboard-field">
        <span className="stm-onboard-field__label">Labels <span className="stm-onboard-optional">(optional)</span></span>
        <input
          value={form.labels}
          onChange={(event) => updateField('labels', event.target.value)}
          placeholder="e.g. V26.2 DI, Sales Ready"
        />
      </label>
    </div>
  );

  const renderStep2 = () => (
    <div className="stm-onboard-step-content">
      <section className="stm-onboard-subsection">
        <h3 className="stm-onboard-subsection__title">
          Tenant Access
          <span className="stm-onboard-section-badge stm-onboard-section-badge--required">Required</span>
        </h3>
        <p className="stm-onboard-subsection__helper">All tenant access fields are required.</p>
        <div className="stm-onboard-fields">
          {ACCESS_FIELDS.map((field) => (
            field.secret
              ? renderSecretField(field.label, field.key, true)
              : renderTextField(field.label, field.key, '', true)
          ))}
        </div>
      </section>

      <section className="stm-onboard-subsection stm-onboard-subsection--integrations">
        <h3 className="stm-onboard-subsection__title">
          Integrations
          <span className="stm-onboard-section-badge stm-onboard-section-badge--optional">Optional</span>
        </h3>
        <p className="stm-onboard-subsection__helper">Configure these only if they are used by this tenant.</p>
      </section>

      <div className="stm-onboard-accordion">
        {INTEGRATION_SECTIONS.map((section) => (
          <div
            key={section.key}
            className={`stm-onboard-accordion__item ${openIntegration === section.key ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="stm-onboard-accordion__trigger"
              onClick={() => setOpenIntegration(openIntegration === section.key ? '' : section.key)}
            >
              <span>{section.label}</span>
              <span className="stm-onboard-accordion__meta">
                <span className="stm-onboard-accordion__badge">Optional</span>
                <FaChevronRight />
              </span>
            </button>
            {openIntegration === section.key && (
              <div className="stm-onboard-accordion__body">
                {section.fields.map((field) => (
                  field.secret
                    ? renderSecretField(field.label, field.key)
                    : renderTextField(field.label, field.key)
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderReviewSection = (title, editStep, integrationKey, children) => (
    <section className="stm-onboard-review__section">
      <div className="stm-onboard-review__header">
        <h4>{title}</h4>
        <button type="button" className="stm-onboard-review__edit" onClick={() => goToStep(editStep, integrationKey)}>
          Edit
        </button>
      </div>
      <div className="stm-onboard-review__body">{children}</div>
    </section>
  );

  const renderStep3 = () => (
    <div className="stm-onboard-review">
      {renderReviewSection('Tenant Details', 1, '', (
        <>
          <ReviewField label="Tenant Type" value={form.tenantType} />
          <ReviewField label="Tenant URL" value={getDisplayUrl(form.tenantUrl)} />
          <ReviewField label="ITSM URL" value={getDisplayUrl(form.itsmUrl)} />
          {form.labels.trim() && (
            <ReviewField label="Labels" value={form.labels} />
          )}
        </>
      ))}

      {renderReviewSection('Tenant Access', 2, '', (
        <>
          <ReviewField label="Username" value={form.username} />
          <ReviewSecretField label="Password" value={form.password} revealed={reviewRevealed.password} onToggle={() => toggleReviewSecret('password')} />
          <ReviewSecretField label="API Key" value={form.apiKey} revealed={reviewRevealed.apiKey} onToggle={() => toggleReviewSecret('apiKey')} />
          <ReviewSecretField label="Access Key" value={form.accessKey} revealed={reviewRevealed.accessKey} onToggle={() => toggleReviewSecret('accessKey')} />
          <ReviewSecretField label="Secret Key" value={form.secretKey} revealed={reviewRevealed.secretKey} onToggle={() => toggleReviewSecret('secretKey')} />
        </>
      ))}

      {renderReviewSection('Jira', 2, 'jira', (
        <>
          <ReviewField label="Jira URL" value={getDisplayUrl(form.jiraUrl)} />
          <ReviewField label="Jira Username" value={form.jiraUsername} />
          <ReviewSecretField label="Jira Password" value={form.jiraPassword} revealed={reviewRevealed.jiraPassword} onToggle={() => toggleReviewSecret('jiraPassword')} />
        </>
      ))}

      {renderReviewSection('Catchpoint', 2, 'catchpoint', (
        <>
          <ReviewField label="Catchpoint URL" value={getDisplayUrl(form.catchpointUrl)} />
          <ReviewField label="Catchpoint Username" value={form.catchpointUsername} />
          <ReviewSecretField label="Catchpoint Password" value={form.catchpointPassword} revealed={reviewRevealed.catchpointPassword} onToggle={() => toggleReviewSecret('catchpointPassword')} />
        </>
      ))}

      {renderReviewSection('Teams Credentials', 2, 'chatops', (
        <>
          <ReviewField label="Teams Username" value={form.chatopsUsername} />
          <ReviewSecretField label="Teams Password" value={form.chatopsPassword} revealed={reviewRevealed.chatopsPassword} onToggle={() => toggleReviewSecret('chatopsPassword')} />
        </>
      ))}

      {renderReviewSection('Dynatrace', 2, 'dynatrace', (
        <>
          <ReviewField label="Dynatrace Host" value={form.dynatraceHost} />
          <ReviewField label="Dynatrace Username" value={form.dynatraceUsername} />
          <ReviewSecretField label="Dynatrace Password" value={form.dynatracePassword} revealed={reviewRevealed.dynatracePassword} onToggle={() => toggleReviewSecret('dynatracePassword')} />
        </>
      ))}
    </div>
  );

  return (
    <div className="stm-modal-overlay" onClick={closeWizard}>
      <div className="stm-modal stm-modal--onboard" onClick={(event) => event.stopPropagation()}>
        <header className="stm-modal__header">
          <div>
            <h2>Onboard Tenant</h2>
            <p>Add a new shared booking tenant</p>
          </div>
          <button type="button" className="stm-icon-btn" onClick={closeWizard} disabled={isSaving} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <nav className="stm-onboard-steps" aria-label="Onboarding steps">
          {ONBOARD_STEPS.map((item) => (
            <div
              key={item.id}
              className={`stm-onboard-steps__item ${step === item.id ? 'is-active' : ''} ${step > item.id ? 'is-complete' : ''}`}
            >
              <span className="stm-onboard-steps__indicator">
                {step > item.id ? <FaCheck /> : item.id}
              </span>
              <span className="stm-onboard-steps__label">{item.label}</span>
            </div>
          ))}
        </nav>

        <div className="stm-modal__body stm-modal__body--onboard">
          {onboardError && <p className="stm-field-error stm-onboard-error">{onboardError}</p>}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        <footer className="stm-modal__footer stm-modal__footer--sticky">
          {step > 1 && (
            <button type="button" className="stm-btn stm-btn--secondary" onClick={() => setStep((prev) => prev - 1)} disabled={isSaving}>
              Back
            </button>
          )}
          <div className="stm-modal__footer-spacer" />
          <button type="button" className="stm-btn stm-btn--secondary" onClick={closeWizard} disabled={isSaving}>Cancel</button>
          {step < 3 ? (
            <button
              type="button"
              className="stm-btn stm-btn--primary"
              onClick={() => { if (validateStep(step)) setStep((prev) => prev + 1); }}
            >
              Continue
            </button>
          ) : (
            <button type="button" className="stm-btn stm-btn--primary" disabled={isSaving} onClick={submit}>
              {isSaving ? 'Onboarding...' : 'Onboard Tenant'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default OnboardTenantWizard;
